import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Building2, CreditCard, Percent, BarChart3, PieChart, ArrowUpRight, Target, Wallet } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart as RePie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';

/* ─── Simulated financial data ─── */
const PLANO_COLORS = { Básico: '#3b82f6', Profissional: '#8b5cf6', Enterprise: '#f59e0b' };

/* ─── KPI Card ─── */
function FinKPI({ icon: Icon, label, value, sub, color, trend }: {
  icon: any; label: string; value: string; sub?: string; color: string; trend?: string;
}) {
  return (
    <div style={{
      background: 'var(--bg-card, white)', borderRadius: 16,
      border: '1px solid var(--border-color, #e5e7eb)', padding: '20px 24px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -10, right: -10, width: 80, height: 80,
        borderRadius: '50%', background: `${color}08`,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${color}15`, color,
        }}>
          <Icon size={20} />
        </div>
        <span style={{ fontSize: '.78rem', color: 'var(--text-muted, #6b7280)', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-main, #111)', marginBottom: 4 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {trend && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 8,
            background: '#10b98115', color: '#10b981', fontSize: '.72rem', fontWeight: 700,
          }}>
            <ArrowUpRight size={12} /> {trend}
          </span>
        )}
        {sub && <span style={{ fontSize: '.75rem', color: 'var(--text-muted, #9ca3af)' }}>{sub}</span>}
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export function MasterFinanceiroPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(ROUTES.master.financeiro)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const d = data || {};
  const mrrCalculated = d.mrrCalculated || 0;
  const arrCalculated = d.arrCalculated || 0;
  const arpu = d.arpu || 0;
  const revenuePerStudent = d.revenuePerStudent || 0;
  const planDistribution: any[] = d.planDistribution || [];
  
  const MRR_DATA = d.mrrData || [];
  const PROJECAO_DATA = d.projecaoData || [];
  const CHURN_DATA = d.churnData || [];

  // Pie chart data
  const pieData: { name: string; value: number; revenue: number }[] = planDistribution.map((p: any) => ({
    name: p.name,
    value: p.escolas,
    revenue: p.escolas * p.preco,
  }));

  if (loading) return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid var(--border-color)', borderTop: '4px solid #8b5cf6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-muted)' }}>Carregando dados financeiros...</p>
      </div>
    </div>
  );

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(139,92,246,.1)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 250, height: 250, borderRadius: '50%', background: 'rgba(16,185,129,.06)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <DollarSign size={18} style={{ color: '#a78bfa' }} />
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>
              Revenue & Metrics
            </span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: '0 0 4px' }}>
            Painel Financeiro
          </h1>
          <p style={{ color: 'rgba(255,255,255,.4)', fontSize: '.82rem', marginBottom: 24 }}>
            Métricas de receita, crescimento e projeções — {new Date().toLocaleDateString('pt-BR')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
            {[
              { icon: Wallet, value: `R$ ${mrrCalculated.toLocaleString('pt-BR')}`, label: 'MRR Atual', color: '#34d399' },
              { icon: TrendingUp, value: `R$ ${arrCalculated.toLocaleString('pt-BR')}`, label: 'ARR Projetado', color: '#a78bfa' },
              { icon: Target, value: `R$ ${arpu.toLocaleString('pt-BR')}`, label: 'ARPU/escola', color: '#fbbf24' },
              { icon: Percent, value: '1.2%', label: 'Churn Rate', color: '#38bdf8' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${s.color}20`, color: s.color, backdropFilter: 'blur(10px)',
                }}>
                  <s.icon size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* MRR Evolution */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={18} style={{ color: '#10b981' }} />
            Evolução do MRR
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={MRR_DATA}>
              <defs>
                <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any) => [`R$ ${Number(value).toLocaleString('pt-BR')}`, 'MRR']}
                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.85rem' }}
              />
              <Area type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={3} fill="url(#mrrGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Plan Distribution */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PieChart size={18} style={{ color: '#8b5cf6' }} />
            Distribuição por Plano
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <RePie>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                dataKey="value"
                paddingAngle={4}
                label={({ name, value }) => `${name} (${value})`}
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={PLANO_COLORS[entry.name as keyof typeof PLANO_COLORS] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any, name: any, props: any) => [`${value} escolas · R$ ${props.payload.revenue.toLocaleString('pt-BR')}/mês`, name]}
                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.8rem' }}
              />
            </RePie>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            {planDistribution.map(p => (
              <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.82rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: PLANO_COLORS[p.name as keyof typeof PLANO_COLORS] }} />
                  <span>{p.name}</span>
                </div>
                <span style={{ fontWeight: 700 }}>R$ {p.preco.toLocaleString('pt-BR')}/mês</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* Revenue Projection */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={18} style={{ color: '#8b5cf6' }} />
            Projeção de Faturamento (8 meses)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={PROJECAO_DATA}>
              <defs>
                <linearGradient id="projGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any) => [`R$ ${Number(value).toLocaleString('pt-BR')}`, 'Receita Projetada']}
                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.85rem' }}
              />
              <Bar dataKey="receita" fill="url(#projGradient)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Unit Economics */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCard size={18} style={{ color: '#f59e0b' }} />
            Unit Economics
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Receita por Aluno', value: `R$ ${revenuePerStudent}`, color: '#10b981' },
              { label: 'CAC (Estimado)', value: 'R$ 120', color: '#3b82f6' },
              { label: 'LTV (12 meses)', value: `R$ ${(arpu * 12).toLocaleString('pt-BR')}`, color: '#8b5cf6' },
              { label: 'LTV/CAC Ratio', value: `${((arpu * 12) / 120).toFixed(1)}x`, color: '#f59e0b' },
              { label: 'Payback (meses)', value: `${(120 / arpu * 30).toFixed(1)}`, color: '#ef4444' },
              { label: 'Margem Bruta', value: '78%', color: '#10b981' },
            ].map(item => (
              <div key={item.label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: 'var(--bg-main, #f9fafb)', borderRadius: 10,
              }}>
                <span style={{ fontSize: '.85rem', color: 'var(--text-muted, #6b7280)' }}>{item.label}</span>
                <span style={{ fontWeight: 800, color: item.color, fontSize: '.95rem' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Churn Chart */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: 16,
        border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Percent size={18} style={{ color: '#3b82f6' }} />
          Evolução do Churn Rate
        </h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={CHURN_DATA}>
            <defs>
              <linearGradient id="churnGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
            <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6b7280' }} />
            <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={v => `${v}%`} domain={[0, 3]} />
            <Tooltip
              formatter={(value: any) => [`${value}%`, 'Churn']}
              contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.85rem' }}
            />
            <Area type="monotone" dataKey="churn" stroke="#3b82f6" strokeWidth={3} fill="url(#churnGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
